import { DeviceMode, type NodeV2 } from '@vedalib/editor'
import type { EditorElement } from '@vedalib/editor/lib/elements'
import React, { useCallback, useMemo, useState } from 'react'

import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import { IconButton } from 'components/uiKit/Button'
import DropDown from 'components/uiKit/Dropdown'
import { KitSize } from 'components/uiKit/KitTypes'
import { updateBlock } from 'services/Store/Project/actions'
import { useProjectDispatch } from 'services/Store/Project/hooks'
import { addNode } from 'services/Store/Project/mutators/node'
import { mergeNodeStyle } from 'services/Store/Project/selectors'
import type { Block, IBlockMode, MapType } from 'services/Store/Project/types'

import Picker from '../../Picker'
import * as s from './Flex.module.scss'

interface IFlexControlsProps {
  block: Block
  node: NodeV2
  mode: IBlockMode
}

const FlexControls: React.FC<IFlexControlsProps> = ({ block, mode, node }) => {
  const dispatch = useProjectDispatch()
  const [showPicker, setShowPicker] = useState(false)
  const everyAbsolute = useMemo(
    () =>
      node.children.every((id) => {
        const node = block.schema.nodes[id] as NodeV2
        const style = mergeNodeStyle(node.style, mode.deviceMode)
        return style.flexFlow === 'absolute'
      }),
    [block.schema.nodes, mode.deviceMode, node.children],
  )

  const onChange = useCallback(
    (mutator: (block: Block) => Block) =>
      dispatch(updateBlock({ id: block?.uuid, value: mutator(block) })),
    [block, dispatch],
  )

  const handleAddChild = useCallback(
    (newNode: NodeV2, data?: { nodes?: MapType<NodeV2>; elements?: MapType<EditorElement> }) => {
      onChange(addNode(node.id, newNode, data))
      setShowPicker(false)
    },
    [onChange, node.id],
  )

  if ((!node.children.length || everyAbsolute) && mode.deviceMode === DeviceMode.desktop) {
    return (
      <AbsolutePortal name='add' placement='center'>
        <div className={s.cell}>
          <DropDown
            onVisibleChange={setShowPicker}
            overlay={<Picker block={block} mode={mode} node={node} onAdd={handleAddChild} />}
            placement='right-start'
            styleType='clear'
            visible={showPicker}
          >
            <IconButton
              background='var(--color-flex)'
              icon='add'
              name='addChild'
              size={KitSize.XS}
              round
            />
          </DropDown>
        </div>
      </AbsolutePortal>
    )
  }

  return null
}

export default React.memo(FlexControls)
